import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { PerformanceBriefPrompt } from '@/hooks/usePerformanceBriefPrompts';

interface PerformanceBriefPromptContextType {
  isOpen: boolean;
  editingPrompt: PerformanceBriefPrompt | null;
  openCreateDialog: () => void;
  openEditDialog: (prompt: PerformanceBriefPrompt) => void;
  closeDialog: () => void;
}

const PerformanceBriefPromptContext = createContext<PerformanceBriefPromptContextType | undefined>(undefined);

export const PerformanceBriefPromptProvider = ({ children }: { children: ReactNode }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [editingPrompt, setEditingPrompt] = useState<PerformanceBriefPrompt | null>(null);

  const openCreateDialog = useCallback(() => {
    setEditingPrompt(null);
    setIsOpen(true);
  }, []);

  const openEditDialog = useCallback((prompt: PerformanceBriefPrompt) => {
    setEditingPrompt(prompt);
    setIsOpen(true);
  }, []);

  const closeDialog = useCallback(() => {
    setIsOpen(false);
    setEditingPrompt(null);
  }, []);

  return (
    <PerformanceBriefPromptContext.Provider
      value={{
        isOpen,
        editingPrompt,
        openCreateDialog,
        openEditDialog,
        closeDialog,
      }}
    >
      {children}
    </PerformanceBriefPromptContext.Provider>
  );
};

export const usePerformanceBriefPromptDialog = () => {
  const context = useContext(PerformanceBriefPromptContext);
  if (!context) {
    throw new Error('usePerformanceBriefPromptDialog must be used within a PerformanceBriefPromptProvider');
  }
  return context;
};
